var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, CircularProgress, TextField, Typography, useTheme, } from "@mui/material";
import { Controller } from "react-hook-form";
import { ErrorMessage } from "./ValidationMessage";
// Debounce hook
function useDebouncedValue(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debounced;
}
export const FSearchableMultiselectAutocomplete = (_a) => {
    var { name, control, label, required = false, options, error, helperText, size = "medium", onSearch, isLoading = false, searchPlaceholder, noOptionsText, minCharactersForSearch = 1, limitTags = 3, searchDebounceMs = 300, isTaskDialog = false, // multiselect only for TaskDialog
    onListboxScroll, onListboxNeedsMore, listboxMaxHeight = "min(320px, 50vh)", ListboxProps } = _a, props = __rest(_a, ["name", "control", "label", "required", "options", "error", "helperText", "size", "onSearch", "isLoading", "searchPlaceholder", "noOptionsText", "minCharactersForSearch", "limitTags", "searchDebounceMs", "isTaskDialog", "onListboxScroll", "onListboxNeedsMore", "listboxMaxHeight", "ListboxProps"]);
    const { t } = useTranslation(["form", "common"]);
    const theme = useTheme();
    const [inputValue, setInputValue] = useState("");
    // Kept separate from inputValue: selecting an option clears the visible input
    // but must not re-run the server search (that would drop the loaded pages).
    const [searchTerm, setSearchTerm] = useState("");
    const hasUserChangedInputRef = useRef(false);
    const effectiveSearchPlaceholder = searchPlaceholder !== null && searchPlaceholder !== void 0 ? searchPlaceholder : t("form:placeholders.searchHere");
    const effectiveNoOptionsText = noOptionsText !== null && noOptionsText !== void 0 ? noOptionsText : t("common:status.noOptions");
    const debouncedSearchTerm = useDebouncedValue(searchTerm, searchDebounceMs);
    // call onSearch for server-side filtering
    useEffect(() => {
        if (!onSearch || !hasUserChangedInputRef.current)
            return;
        if (debouncedSearchTerm.length >= minCharactersForSearch) {
            onSearch(debouncedSearchTerm);
        }
        else if (debouncedSearchTerm.length === 0) {
            onSearch("");
        }
    }, [debouncedSearchTerm, minCharactersForSearch, onSearch]);
    // The listbox only pages on scroll, so it has to overflow. On tall screens a
    // page can fit without a scrollbar and no scroll event ever fires — measure
    // the mounted listbox and ask for the next page until it does overflow.
    const [listboxEl, setListboxEl] = useState(null);
    const lastFillCountRef = useRef(-1);
    // A new search replaces the options, so the previous "no new rows" result
    // no longer applies. Must stay above the fill effect to run first.
    useEffect(() => {
        lastFillCountRef.current = -1;
    }, [debouncedSearchTerm]);
    useEffect(() => {
        if (!listboxEl) {
            lastFillCountRef.current = -1;
            return;
        }
        if (!onListboxNeedsMore || isLoading)
            return;
        // A page that added nothing means we are done — never ask twice for it.
        if (lastFillCountRef.current === options.length)
            return;
        const requestMoreIfNotScrollable = () => {
            if (listboxEl.scrollHeight - listboxEl.clientHeight < 8) {
                lastFillCountRef.current = options.length;
                onListboxNeedsMore();
            }
        };
        requestMoreIfNotScrollable();
        // Re-check on zoom, window resize and orientation change.
        const observer = new ResizeObserver(requestMoreIfNotScrollable);
        observer.observe(listboxEl);
        return () => observer.disconnect();
    }, [listboxEl, options.length, isLoading, onListboxNeedsMore]);
    return (React.createElement(Controller, { name: name, control: control, rules: props.rules, render: (_a) => {
            var _b;
            var _c = _a.field, { onChange, value } = _c, field = __rest(_c, ["onChange", "value"]), { fieldState: { error: fieldError } } = _a;
            const multiple = !!isTaskDialog;
            const selectedOptions = multiple
                ? Array.isArray(value)
                    ? value // cast value to IOption[]
                    : []
                : value
                    ? [value]
                    : [];
            // const mergedOptions = options;
            return (React.createElement(Box, null,
                React.createElement(Autocomplete, Object.assign({}, field, { limitTags: limitTags }, props, { multiple: multiple, options: options, value: multiple ? selectedOptions : (_b = selectedOptions[0]) !== null && _b !== void 0 ? _b : null, inputValue: inputValue, onInputChange: (_, newInput, reason) => {
                        if (reason === "input") {
                            hasUserChangedInputRef.current = true;
                            setInputValue(newInput);
                            setSearchTerm(newInput);
                        }
                    }, onChange: (_, newValue) => {
                        if (multiple) {
                            const newValues = Array.isArray(newValue)
                                ? newValue.map((o) => (Object.assign({ value: o.value, label: o.label }, (o.nodeTypeCode !== undefined && { nodeTypeCode: o.nodeTypeCode })))) // store full object
                                : [];
                            onChange(newValues);
                        }
                        else {
                            onChange(newValue
                                ? Object.assign({ value: newValue.value, label: newValue.label }, (newValue.nodeTypeCode !== undefined && {
                                    nodeTypeCode: newValue.nodeTypeCode,
                                })) : null);
                        }
                        // Clear the visible input only. searchTerm is left alone so the
                        // loaded (and scrolled) list survives selecting an option.
                        setInputValue("");
                    }, getOptionLabel: (option) => option.label, isOptionEqualToValue: (option, val) => option.value === (val === null || val === void 0 ? void 0 : val.value), loading: isLoading, noOptionsText: isLoading ? (React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                        React.createElement(CircularProgress, { size: 16 }),
                        React.createElement(Typography, { variant: "body2" }, t("common:status.loading")))) : inputValue.length < minCharactersForSearch ? (React.createElement(Typography, { variant: "body2", color: "text.secondary" }, t("common:status.typeToSearch", { count: minCharactersForSearch }))) : (effectiveNoOptionsText), renderInput: (params) => (React.createElement(TextField, Object.assign({}, params, { label: React.createElement(React.Fragment, null,
                            label,
                            required && (React.createElement("span", { style: { color: "red" } }, "\u00A0*"))), placeholder: multiple && selectedOptions.length > 0
                            ? ""
                            : effectiveSearchPlaceholder, error: !!fieldError || !!error, fullWidth: true, size: size, variant: "filled", InputProps: Object.assign(Object.assign({}, params.InputProps), { endAdornment: (React.createElement(React.Fragment, null,
                                isLoading && (React.createElement(CircularProgress, { color: "inherit", size: 20 })),
                                params.InputProps.endAdornment)) }) }))), filterOptions: (opts) => opts, slotProps: Object.assign(Object.assign({}, props.slotProps), { listbox: Object.assign(Object.assign({}, ListboxProps), { 
                            // Forked with MUI's own listbox ref, so measuring is safe.
                            ref: setListboxEl, sx: Object.assign({ maxHeight: listboxMaxHeight }, ListboxProps === null || ListboxProps === void 0 ? void 0 : ListboxProps.sx), onScroll: (e) => {
                                var _a;
                                (_a = ListboxProps === null || ListboxProps === void 0 ? void 0 : ListboxProps.onScroll) === null || _a === void 0 ? void 0 : _a.call(ListboxProps, e);
                                onListboxScroll === null || onListboxScroll === void 0 ? void 0 : onListboxScroll(e);
                            } }) }) })),
                React.createElement(ErrorMessage, { error: (fieldError === null || fieldError === void 0 ? void 0 : fieldError.message) || (error === null || error === void 0 ? void 0 : error.message) || helperText })));
        } }));
};
